import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import QRCode from 'react-qr-code'

import { Info } from '@maistodos/core-icons'
import {
  Box,
  Button,
  Radio,
  RadioGroup,
  Skeleton,
  toast,
  Typography,
} from '@maistodos/design-system-web'

import {
  AlertContainer,
  AlertContent,
  AlertIconWrapper,
  AlertMessage,
  AlertTitle,
} from '@/components/Alert'
import { FeedbackModal } from '@/components/FeedbackModal'
import {
  LinkStatus,
  PaymentOption,
} from '@/modules/checkout/_core/domain/entities'
import { SectionGroup } from '@/modules/checkout/components/Section/SectionGroup'
import { SectionText } from '@/modules/checkout/components/Section/SectionText'
import { SectionTitle } from '@/modules/checkout/components/Section/SectionTitle'
import { useLink } from '@/modules/checkout/hooks/use-link'
import { useModal } from '@/modules/checkout/hooks/use-modal'
import { usePayGenerate } from '@/modules/checkout/hooks/use-pay-generate'
import { PaymentFlowAnalytics } from '@/shared/utils/analytics'
import { copyToClipboard } from '@/shared/utils/copy-to-clipboard'
import { formatLocaleCurrency } from '@/shared/utils/format-locale'
import { cn } from '@/shared/utils/tailwind'

import { FIVE_SECONDS, REFETCH_RUN_LIMIT } from './constants'
import { FormPixProps, PixOption } from './types'

export const FormPix = ({ id, amount, pix, automaticPix }: FormPixProps) => {
  const { t, i18n } = useTranslation()

  const refetchTimes = useRef<number>(0)
  const [refetchInterval, setRefetchInterval] = useState<number | false>(0)
  const [selected, setSelected] = useState<PixOption | null>(null)

  const hasBothOptions = Boolean(pix && automaticPix)

  const selectedPaymentType = useMemo(() => {
    if (selected === PaymentOption.AutomaticPixCopyPaste) return automaticPix
    if (selected === PaymentOption.Pix) return pix

    return undefined
  }, [selected, pix, automaticPix])

  useEffect(() => {
    if (hasBothOptions) return

    setSelected(
      pix
        ? PaymentOption.Pix
        : automaticPix
          ? PaymentOption.AutomaticPixCopyPaste
          : null,
    )
  }, [hasBothOptions, pix, automaticPix])

  const onChangeType = useCallback(
    (event_: React.ChangeEvent<HTMLInputElement>) => {
      const value = event_.target.value as PixOption

      PaymentFlowAnalytics.onClickPixType(value)

      refetchTimes.current = 0
      setRefetchInterval(0)

      setSelected(value)
    },
    [],
  )

  const {
    isOpen: isOpenErrorModal,
    open: openErrorModal,
    close: closeErrorModal,
  } = useModal()

  const { data, isLoading, error, isError } = usePayGenerate(
    selected ?? PaymentOption.Pix,
    id,
    selectedPaymentType?.id,
  )

  const code = useMemo(() => data?.pix?.qrCode || '', [data])

  const formatted = useMemo<string>(
    () => formatLocaleCurrency(amount, i18n.language),
    [amount, i18n.language],
  )

  const isAutomatic = selected === PaymentOption.AutomaticPixCopyPaste

  const notice = useMemo(() => {
    if (isAutomatic) {
      return t(
        'Mais praticidade: autorize este e os próximos pagamentos automaticamente, sem precisar gerar uma nova autorização a cada mês.',
      )
    }

    if (!hasBothOptions) {
      return null
    }

    const [before, after] = t(
      'Pagamento único. {destaque} para não precisar autorizar novamente todos os meses.',
    ).split('{destaque}')

    return (
      <>
        {before}
        <strong>{t('Prefira o Pix Automático')}</strong>
        {after}
      </>
    )
  }, [hasBothOptions, isAutomatic, t])

  const errorTitle = useMemo(() => {
    if (error?.needs_to_pay_with_max_installments?.length) {
      return error.needs_to_pay_with_max_installments[0]
    }

    return t('Não foi possível processar seu pagamento.')
  }, [error, t])

  const onCopy = useCallback(async () => {
    PaymentFlowAnalytics.onClickCopyCode()

    await copyToClipboard(code)

    toast.success({
      message: t('Cópia realizada com sucesso!'),
      duration: 3000,
    })
  }, [code, t])

  useEffect(() => {
    if (isError) {
      openErrorModal()
    }
  }, [isError, openErrorModal])

  // Long-polling to automatically redirect to the receipt screen if Pix got confirmed
  const { isFetching, data: link } = useLink(id, {
    notifyOnChangeProps: ['isFetching', 'data'],
    refetchInterval,
  })

  useEffect(() => {
    if (code) {
      setRefetchInterval(FIVE_SECONDS)
    }
  }, [code])

  useEffect(() => {
    if (isFetching) {
      refetchTimes.current++
    }

    if (refetchTimes.current >= REFETCH_RUN_LIMIT) {
      setRefetchInterval(false)
    }
  }, [isFetching])

  const linkStatus = link?.status

  useEffect(() => {
    if (linkStatus === LinkStatus.Paid && selected) {
      PaymentFlowAnalytics.onApprovedPayment(selected, id)
    }
  }, [id, linkStatus, selected])

  return (
    <div className="flex flex-col gap-spacing24">
      {hasBothOptions && (
        <RadioGroup direction="row" name="pix-option">
          <Radio
            label={t('Pix Automático')}
            value={PaymentOption.AutomaticPixCopyPaste}
            checked={selected === PaymentOption.AutomaticPixCopyPaste}
            onClick={onChangeType}
          />
          <Radio
            label={t('Pix Avulso')}
            value={PaymentOption.Pix}
            checked={selected === PaymentOption.Pix}
            onClick={onChangeType}
          />
        </RadioGroup>
      )}

      {selected && (
        <div
          className={cn(
            'flex w-full flex-col gap-spacing24',
            automaticPix
              ? 'rounded-medium border-2 border-brand500 p-spacing16 md:w-3/4'
              : 'md:w-1/2',
          )}
        >
          {isAutomatic && (
            <span className="w-fit rounded-pill bg-purple100 px-spacing12 py-spacing4 text-caption font-semibold text-purple700">
              {t('Mais recomendado')}
            </span>
          )}

          {notice && (
            <AlertContainer
              variant="success"
              className="rounded-medium px-spacing24 py-spacing16"
            >
              <AlertContent>
                <AlertMessage>{notice}</AlertMessage>
              </AlertContent>
            </AlertContainer>
          )}

          {isLoading && (
            <div className="flex w-full flex-col gap-spacing24">
              <SectionGroup>
                <SectionTitle>{t('Valor do PIX')}</SectionTitle>
                <SectionText>{formatted}</SectionText>
              </SectionGroup>
              <SectionGroup>
                <SectionTitle>{t('Chave copia e cola:')}</SectionTitle>
                <Skeleton height={16} />
                <Skeleton height={16} />
                <Skeleton height={16} />
                <Skeleton height={16} />
              </SectionGroup>
              <Button variant="primary" fullWidth disabled loading>
                {t('Copiar chave')}
              </Button>
              <div className="flex flex-col items-center gap-spacing4">
                <Skeleton height={180} width={180} />
                <div className="flex flex-col items-center gap-spacing2">
                  <Skeleton height={12} width={150} />
                  <Skeleton height={12} width={50} />
                </div>
              </div>
            </div>
          )}

          {code ? (
            <div className="flex w-full flex-col gap-spacing24">
              <SectionGroup>
                <SectionTitle>{t('Valor do PIX')}</SectionTitle>
                <SectionText>{formatted}</SectionText>
              </SectionGroup>

              <SectionGroup>
                <SectionTitle>{t('Chave copia e cola:')}</SectionTitle>
                <SectionText className="break-words">{code}</SectionText>
              </SectionGroup>

              <Button
                variant="primary"
                onClick={onCopy}
                testID="copy"
                fullWidth
              >
                {t('Copiar chave')}
              </Button>

              <div className="mx-auto flex w-full max-w-[180px] flex-col">
                <Box className="mx-auto my-spacing0 h-auto w-full max-w-full">
                  <QRCode
                    size={256}
                    className={'h-auto w-full max-w-full'}
                    viewBox="0 0 256 256"
                    value={code}
                  />
                </Box>

                <Typography variant="caption" align="center">
                  {t('Mire a câmera do seu celular para o QRCODE')}
                </Typography>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {isError && (
        <AlertContainer variant="warning">
          <AlertIconWrapper>
            <Info color="currentColor" fontSize="24px" />
          </AlertIconWrapper>
          <AlertContent>
            {errorTitle ? <AlertTitle>{errorTitle}</AlertTitle> : null}
            <AlertMessage>
              {t(
                'Verifique as informações do seu pagamento e tente novamente.',
              )}
            </AlertMessage>
          </AlertContent>
        </AlertContainer>
      )}

      <FeedbackModal
        type="error"
        isOpen={isOpenErrorModal}
        onClose={closeErrorModal}
        title={errorTitle}
        description={t(
          'Verifique as informações do seu pagamento e tente novamente.',
        )}
        footer={
          <Button variant="secondary" fullWidth onClick={closeErrorModal}>
            Fechar
          </Button>
        }
      />
    </div>
  )
}
